import { apiUrl } from "@/utils/api.ts";
import { localZoneId } from "@/utils/datetime.ts";
import { queryOptions } from "@tanstack/react-query";
import { createDataSelector, createResourceSelector, handleJsonApiError } from "jsonapi-zod-query";
import { z } from "zod";
import { zj } from "zod-joda";

const bidTargetSchema = z.enum(["parcel", "parcel_combination", "global"]);
export type BidTarget = z.output<typeof bidTargetSchema>;

const bidSchema = z.object({
    registrationId: z.string(),
    target: bidTargetSchema,
    index: z.number().int().nonnegative(),
    amount: z.number().int().nonnegative(),
    previousAmount: z.number().int().nonnegative().nullable(),
    placedOn: zj.zonedDateTime().nullable(),
    previousPlacedOn: zj.zonedDateTime().nullable(),
});

export type Bid = z.output<typeof bidSchema>;

const biddingIncrementSchema = z.object({
    target: bidTargetSchema,
    index: z.number().int().nonnegative(),
    amount: z.number().int().positive(),
});

export type BiddingIncrement = z.output<typeof biddingIncrementSchema>;

const winningBidSchema = z.object({
    registrationId: z.string(),
    target: bidTargetSchema,
    index: z.number().int().nonnegative(),
    amount: z.number().int().nonnegative(),
});

export const auctionSessionAttributesSchema = z.object({
    startedAt: zj.zonedDateTime().transform((value) => value.withZoneSameInstant(localZoneId)),
    concludedAt: zj
        .zonedDateTime()
        .transform((value) => value.withZoneSameInstant(localZoneId))
        .nullable(),
    bids: z.array(bidSchema),
    lastBid: bidSchema.nullable(),
    biddingIncrements: z.array(biddingIncrementSchema),
    winningBids: z.array(winningBidSchema),
    leaderOverrideId: z.string().nullable(),
    leaderOverrideAllowed: z.boolean(),
});

export const auctionSessionSelector = createDataSelector(
    createResourceSelector({
        type: "auction_session",
        attributesSchema: auctionSessionAttributesSchema,
    }),
);

export type AuctionSession = ReturnType<typeof auctionSessionSelector>;

export const createAuctionSessionQueryOptionsFactory = (authFetch: typeof fetch) => ({
    get: (auctionId: string) =>
        queryOptions({
            queryKey: ["auction", auctionId, "session"],
            queryFn: async ({ signal }) => {
                const response = await authFetch(apiUrl(`/auctions/${auctionId}/session`), {
                    signal,
                });
                await handleJsonApiError(response);
                return auctionSessionSelector(await response.json());
            },
        }),
    getPreview: (auctionId: string) =>
        queryOptions({
            queryKey: ["auction", auctionId, "session", "preview"],
            queryFn: async ({ signal }) => {
                const response = await authFetch(apiUrl(`/auctions/${auctionId}/session/preview`), {
                    signal,
                });
                await handleJsonApiError(response);
                return auctionSessionSelector(await response.json());
            },
        }),
});
